/**
 * Better Auth client wrappers for the admin app. Sign-in and session
 * lookup go through the backoffice-only `/api/admin-auth/*` endpoints,
 * which use separate admin_* tables and cookies from tenant auth.
 */

import { createAuthClient } from 'better-auth/client';
import { twoFactorClient } from 'better-auth/client/plugins';
import { passkeyClient } from '@better-auth/passkey/client';

const API_PATH = import.meta.env.VITE_API_PATH ?? '/api';
const AUTH_BASE = `${API_PATH}/admin-auth`;

export const authClient = createAuthClient({
  baseURL: AUTH_BASE,
  plugins: [twoFactorClient(), passkeyClient()],
});

export interface SessionUser {
  id: string;
  email: string;
  name?: string | null;
  emailVerified: boolean;
  twoFactorEnabled?: boolean;
  lastLoginMethod?: string | null;
}

export interface SignInResult {
  twoFactorRedirect: boolean;
  twoFactorMethods: string[];
}

export async function getSession(): Promise<SessionUser | null> {
  const res = await fetch(`${AUTH_BASE}/get-session`, { credentials: 'include' });
  if (!res.ok) {
    return null;
  }
  const data = (await res.json().catch(() => null)) as { user?: SessionUser } | null;
  return data?.user ?? null;
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const result = await authClient.signIn.email({ email, password });
  if (result.error) {
    throw new Error(result.error.message ?? 'Sign-in failed');
  }
  const data = result.data as { twoFactorRedirect?: boolean; twoFactorMethods?: string[] } | null;
  return {
    twoFactorRedirect: data?.twoFactorRedirect === true,
    twoFactorMethods: data?.twoFactorMethods ?? [],
  };
}

export async function signInWithPasskey(): Promise<void> {
  const result = await authClient.signIn.passkey({ autoFill: false });
  if (result?.error) {
    throw new Error(result.error.message ?? 'Passkey sign-in failed');
  }
}

export async function verifyTotp(code: string, trustDevice: boolean): Promise<void> {
  const result = await authClient.twoFactor.verifyTotp({
    code,
    trustDevice,
  });
  if (result.error) {
    throw new Error(result.error.message ?? 'Verification failed');
  }
}

export async function verifyBackupCode(code: string, trustDevice: boolean): Promise<void> {
  const result = await authClient.twoFactor.verifyBackupCode({
    code,
    trustDevice,
  });
  if (result.error) {
    throw new Error(result.error.message ?? 'Backup code verification failed');
  }
}

export async function signOut(): Promise<void> {
  await fetch(`${AUTH_BASE}/sign-out`, {
    method: 'POST',
    credentials: 'include',
  });
}
